import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import {
  CryptoAlertRow,
  CryptoAlertTableHeader,
} from "@/components/alerts/CryptoAlertRow";
import {
  AlertsFilters,
  EMPTY_ALERT_FILTERS,
  filterAlerts,
  type AlertFilterState,
} from "@/components/alerts/AlertsFilters";
import { useSavedAlerts } from "@/hooks/useSavedAlerts";
import { TabSelector } from "@/components/TabSelector";
import {
  useInfiniteListings,
  useInfiniteDelistings,
} from "@/hooks/useCryptoAlerts";
import {
  CryptoAlertData,
  PositionData,
  ClosedPositionData,
} from "@crypto-trading/common/browser";
import { Loader2 } from "lucide-react";
import { AlertsStatsRow } from "@/components/AlertsStatsRow";
import { AlertsInsightsSidebar } from "@/components/AlertsInsightsSidebar";
import { AssetClassToggle } from "@/components/AssetClassToggle";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { trackEvent } from "@/utils/analytics";
import { DesktopLayout } from "@/layouts";
import { AlertPositionsDrawer } from "@/components/AlertPositionsDrawer";

// Desktop-specific drawer wrappers
import { AlertDetailDrawerDesktop } from "./drawers/AlertDetailDrawer.Desktop";
import { PositionDetailDrawerDesktop } from "./drawers/PositionDetailDrawer.Desktop";
import { PositionTradeDrawerDesktop } from "./drawers/PositionTradeDrawer.Desktop";
import { TickerSymbolDrawerDesktop } from "./drawers/TickerSymbolDrawer.Desktop";
import { TokenAnalysisDrawerDesktop } from "./drawers/TokenAnalysisDrawer.Desktop";

const ALERTS_PER_PAGE = 10; // More alerts per page on desktop

/**
 * Desktop version of the Alerts page
 * - No right sidebar
 * - 2-column grid for alerts (524px each)
 * - Analyze Token button in header
 * - Right-side sheets instead of bottom drawers
 */
export const AlertsPageDesktop: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [activeTab, setActiveTab] = useState<"listings" | "delistings">(
    "listings"
  );
  const [selectedPosition, setSelectedPosition] = useState<
    PositionData | ClosedPositionData | null
  >(null);
  const [selectedAlert, setSelectedAlert] = useState<CryptoAlertData | null>(
    null
  );
  const [positionsAlert, setPositionsAlert] = useState<CryptoAlertData | null>(
    null
  );
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const [isTickerDrawerOpen, setIsTickerDrawerOpen] = useState(false);
  const [isAnalysisDrawerOpen, setIsAnalysisDrawerOpen] = useState(false);
  const [analysisSymbol, setAnalysisSymbol] = useState<string | null>(null);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  // React Query hooks
  const {
    data: listingsData,
    isLoading: isLoadingListings,
    fetchNextPage: fetchNextListings,
    hasNextPage: hasNextListings,
    isFetchingNextPage: isFetchingNextListings,
  } = useInfiniteListings(
    userId,
    ALERTS_PER_PAGE,
    activeTab === "listings",
    undefined
  );

  const {
    data: delistingsData,
    isLoading: isLoadingDelistings,
    fetchNextPage: fetchNextDelistings,
    hasNextPage: hasNextDelistings,
    isFetchingNextPage: isFetchingNextDelistings,
  } = useInfiniteDelistings(
    userId,
    ALERTS_PER_PAGE,
    activeTab === "delistings",
    undefined
  );

  // Flatten alerts from all pages
  const alerts =
    activeTab === "listings"
      ? (listingsData?.pages.flatMap((page) => page.alerts) ?? [])
      : (delistingsData?.pages.flatMap((page) => page.alerts) ?? []);

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<AlertFilterState>(EMPTY_ALERT_FILTERS);
  const { saved } = useSavedAlerts();
  const visibleAlerts = filterAlerts(alerts, query, filters, saved);

  const isLoading =
    activeTab === "listings" ? isLoadingListings : isLoadingDelistings;
  const hasMore =
    activeTab === "listings"
      ? (hasNextListings ?? false)
      : (hasNextDelistings ?? false);
  const isFetchingNextPage =
    activeTab === "listings"
      ? isFetchingNextListings
      : isFetchingNextDelistings;
  const fetchNextPage =
    activeTab === "listings" ? fetchNextListings : fetchNextDelistings;

  const handleOpenTradeDrawer = (alert: CryptoAlertData) => {
    setTradeSymbol(alert.currency ? ensureUsdtSymbol(alert.currency) : null);
    setIsTradeDrawerOpen(true);
  };

  const handleOpenAnalysisDrawer = () => {
    trackEvent("custom_trade_attempt", { page: "alerts", userId });
    setIsTickerDrawerOpen(true);
  };

  const handleTickerSubmit = useCallback((normalizedSymbol: string) => {
    setAnalysisSymbol(normalizedSymbol);
    setIsTickerDrawerOpen(false);
    setIsAnalysisDrawerOpen(true);
  }, []);

  const handleTradeFromAnalysis = useCallback((symbol: string) => {
    setTradeSymbol(symbol);
    setIsAnalysisDrawerOpen(false);
    setIsTradeDrawerOpen(true);
  }, []);

  useEffect(() => {
    if (selectedAlert) {
      trackEvent("alert_drawer_view", {
        userId,
        alertId: selectedAlert.id,
        symbol: selectedAlert.currency,
        alertType: selectedAlert.type,
        tier: selectedAlert.tier ?? null,
        exchange: selectedAlert.exchange,
      });
    }
  }, [selectedAlert?.id]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || isLoading || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoading, isFetchingNextPage, activeTab, fetchNextPage]);

  return (
    <>
      <DesktopLayout
        pageTitle="Market Alerts"
        pageDescription="Real-time cryptocurrency listing and delisting alerts across global exchanges."
        search={{
          value: query,
          onChange: setQuery,
          placeholder: "Search assets, exchanges...",
        }}
        rightSidebar={
          <AlertsInsightsSidebar
            onAnalyze={handleOpenAnalysisDrawer}
            onViewAll={() => {
              setActiveTab("listings");
              setFilters(EMPTY_ALERT_FILTERS);
              setQuery("");
            }}
          />
        }
      >
        <div className="flex flex-col gap-6">
          <AlertsStatsRow size="lg" onUpcomingClick={() => navigate("/analyze")} />

          <div className="flex items-center gap-3">
            <div className="flex flex-1 items-center gap-4">
              <AssetClassToggle
                value="crypto"
                variant="lg"
                onChange={(value) => {
                  if (value === "stocks") navigate("/alerts/stocks");
                }}
                className="w-[220px]"
              />
              <TabSelector
                variant="lg"
                options={[
                  { value: "listings", label: "Listing" },
                  { value: "delistings", label: "Delisting" },
                ]}
                value={activeTab}
                onChange={setActiveTab}
                className="w-[220px]"
              />
            </div>
            <AlertsFilters alerts={alerts} value={filters} onChange={setFilters} />
          </div>

          <div className="flex flex-col gap-3">
            <CryptoAlertTableHeader />

            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
              </div>
            )}

            {!isLoading && visibleAlerts.length === 0 && (
              <div className="glass py-12 text-center">
                <p className="text-text-secondary">
                  {alerts.length === 0 ? `No ${activeTab} found` : "Nothing matches your search or filters"}
                </p>
              </div>
            )}

            {!isLoading &&
              visibleAlerts.map((alert) => (
                <CryptoAlertRow
                  key={alert.id}
                  alert={alert}
                  onViewPositions={setPositionsAlert}
                  onDetailsClick={() => setSelectedAlert(alert)}
                  onTrade={handleOpenTradeDrawer}
                />
              ))}
          </div>
        </div>

        {/* Load More Trigger */}
        {!isLoading && hasMore && (
          <div ref={loadMoreRef} className="flex justify-center py-8">
            {isFetchingNextPage && (
              <Loader2 className="w-6 h-6 animate-spin text-accent" />
            )}
          </div>
        )}

        {/* End of List */}
        {!isLoading && !hasMore && alerts.length > 0 && (
          <div className="text-center py-8">
            <p className="text-text-secondary text-sm">
              No more {activeTab} to load
            </p>
          </div>
        )}
      </DesktopLayout>

      {/* Desktop Sheet Drawers */}
      <AlertDetailDrawerDesktop
        userId={userId}
        alert={selectedAlert}
        open={!!selectedAlert}
        onOpenChange={(open) => !open && setSelectedAlert(null)}
        onTrade={(alert) => {
          setTradeSymbol(
            alert.currency ? ensureUsdtSymbol(alert.currency) : null
          );
          setIsTradeDrawerOpen(true);
        }}
        onViewPositions={setPositionsAlert}
      />

      <AlertPositionsDrawer
        alert={positionsAlert}
        open={!!positionsAlert}
        onOpenChange={(open) => !open && setPositionsAlert(null)}
        onSelectPosition={(position) => {
          setPositionsAlert(null);
          setSelectedPosition(position);
        }}
        onTrade={(alert) => {
          setPositionsAlert(null);
          handleOpenTradeDrawer(alert);
        }}
      />

      <PositionDetailDrawerDesktop
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        hideCloseAction={true}
        source="alerts_page"
      />

      <PositionTradeDrawerDesktop
        userId={userId}
        symbol={tradeSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => {
          setIsTradeDrawerOpen(open);
          if (!open) {
            setTradeSymbol(null);
          }
        }}
        onGoToPosition={(position) => {
          if (position) {
            setSelectedPosition(position);
          }
        }}
      />

      <TickerSymbolDrawerDesktop
        open={isTickerDrawerOpen}
        onOpenChange={setIsTickerDrawerOpen}
        onSubmit={handleTickerSubmit}
        title="Analyze Token"
        submitButtonText="Analyze"
      />

      <TokenAnalysisDrawerDesktop
        userId={userId}
        symbol={analysisSymbol}
        open={isAnalysisDrawerOpen}
        onOpenChange={(open) => {
          setIsAnalysisDrawerOpen(open);
          if (!open) {
            setAnalysisSymbol(null);
          }
        }}
        onTrade={handleTradeFromAnalysis}
      />
    </>
  );
};
